import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@localagent/ui/components/select';
import { Button } from '@localagent/ui/components/button';
import type { ExecutionCadence, ExecutionCadenceStep } from '@localagent/sdk';

export const EXECUTION_CADENCE_STEPS: ReadonlyArray<{
  value: ExecutionCadenceStep;
  label: string;
}> = [
  { value: '30s', label: '30 seconds' },
  { value: '1m', label: '1 minute' },
  { value: '5m', label: '5 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
];

export function ExecutionCadenceSection({
  sweep,
  reaper,
  saving = false,
  error = null,
  onSweepChange,
  onReaperChange,
  onSave,
}: {
  sweep: ExecutionCadenceStep;
  reaper: ExecutionCadenceStep;
  saving?: boolean;
  error?: string | null;
  onSweepChange: (value: ExecutionCadenceStep) => void;
  onReaperChange: (value: ExecutionCadenceStep) => void;
  onSave: (cadence: ExecutionCadence) => void;
}) {
  return (
    <section className="space-y-4" data-testid="execution-cadence-section">
      <div>
        <h2 className="text-lg font-semibold">Execution cadence</h2>
        <p className="text-sm text-muted-foreground">
          How often the account wakes the schedule sweep and the project-run reaper.
        </p>
      </div>
      <div className="rounded-lg bg-card p-4 flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-sm">
          <span className="font-medium text-muted-foreground">Schedule sweep</span>
          <Select value={sweep} onValueChange={(value) => onSweepChange(value as ExecutionCadenceStep)}>
            <SelectTrigger className="w-44" data-testid="execution-cadence-sweep">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXECUTION_CADENCE_STEPS.map((step) => (
                <SelectItem key={step.value} value={step.value}>
                  {step.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-medium text-muted-foreground">Project-run reaper</span>
          <Select value={reaper} onValueChange={(value) => onReaperChange(value as ExecutionCadenceStep)}>
            <SelectTrigger className="w-44" data-testid="execution-cadence-reaper">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXECUTION_CADENCE_STEPS.map((step) => (
                <SelectItem key={step.value} value={step.value}>
                  {step.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <Button
          size="sm"
          data-testid="execution-cadence-save"
          disabled={saving}
          onClick={() => onSave({ schedule_sweep: sweep, project_run_reaper: reaper })}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
